import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { RootStoreContext } from 'src/stores/RootStore';
import { PopupPriority } from 'src/stores/PopupStore';
import { CheckinSuggestion } from 'src/api/API';
import EarlyCheckinPromoLine from './registration/select-items/EarlyCheckinPromoLine';
import './CheckinSuggestionDialog.css';

const POPUP_ID = 'checkin-suggestion-dialog';

const pad = (n: number) => String(n).padStart(2, '0');

const formatWhen = (s: CheckinSuggestion): string => {
  const d = new Date(`${s.date}T00:00:00`);
  const dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startMin = s.hour * 60 + s.minute;
  const endMin = startMin + (s.duration || 0);
  const endHour = Math.floor(endMin / 60) % 24;
  const endMinute = endMin % 60;
  const start = `${pad(s.hour)}:${pad(s.minute)}`;
  const end = `${pad(endHour)}:${pad(endMinute)}`;
  return s.duration ? `${dateLabel}, ${start}–${end}` : `${dateLabel}, ${start}`;
};

const CheckinSuggestionDialog = observer(() => {
  const { suggestionStore, popupStore } = useContext(RootStoreContext);
  const [isConfirming, setIsConfirming] = useState(false);

  const { suggestions, shouldDisplay } = suggestionStore;
  const active = shouldDisplay && suggestions.length > 0;

  useEffect(() => {
    if (active) {
      popupStore.register(POPUP_ID, PopupPriority.CHECKIN_SUGGESTION);
      return () => popupStore.unregister(POPUP_ID);
    }
  }, [active, popupStore]);

  if (!active) return null;
  if (!popupStore.isActive(POPUP_ID)) return null;

  const handleClose = () => {
    suggestionStore.dismiss();
  };

  const handleConfirm = async () => {
    if (isConfirming || suggestionStore.selectedKeys.length === 0) return;
    setIsConfirming(true);
    try {
      await suggestionStore.confirmAndClose();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop
      keyboard
      centered
      dialogClassName="checkin-suggestion-dialog"
      contentClassName="bg-white"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="checkin-suggestion-title">Book your next classes now and save</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <EarlyCheckinPromoLine />

        {suggestionStore.notice && (
          <div className="checkin-suggestion-notice" role="status" aria-live="polite">
            {suggestionStore.notice}
          </div>
        )}

        {suggestions.length > 1 && (
          <div className="checkin-suggestion-actions-top">
            <button
              type="button"
              className="checkin-suggestion-selectall"
              onClick={() =>
                suggestionStore.allSelected ? suggestionStore.deselectAll() : suggestionStore.selectAll()
              }
            >
              {suggestionStore.allSelected ? 'Unselect all' : 'Select all'}
            </button>
          </div>
        )}

        <ul className={`checkin-suggestion-list ${suggestions.length > 4 ? 'scrollable' : ''}`}>
          {suggestions.map((s) => {
            const selected = suggestionStore.isSelected(s);
            const unaffordable = !selected && !suggestionStore.isAffordable(s);
            const ariaLabel =
              `${s.trainingTitle}, ${formatWhen(s)}, Ȼ${s.discountedCoins}, save Ȼ${s.savingCoins}` +
              (unaffordable ? ', not enough coins' : '');
            return (
              <li
                key={`${s.trainingPeriodId}-${s.date}-${s.hour}-${s.minute}`}
                className={`checkin-suggestion-row ${selected ? 'selected' : ''} ${
                  unaffordable ? 'unaffordable' : ''
                }`}
                role="checkbox"
                aria-checked={selected}
                aria-label={ariaLabel}
                tabIndex={0}
                onClick={() => suggestionStore.toggle(s)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    suggestionStore.toggle(s);
                  }
                }}
              >
                <input
                  type="checkbox"
                  className="checkin-suggestion-checkbox"
                  checked={selected}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <div className="checkin-suggestion-info">
                  <div className="checkin-suggestion-row-title">{s.trainingTitle}</div>
                  <div className="checkin-suggestion-when">{formatWhen(s)}</div>
                  {s.teachers && <div className="checkin-suggestion-teachers">{s.teachers}</div>}
                </div>
                <div className="checkin-suggestion-price">
                  <div className="checkin-suggestion-coins">
                    <span className="checkin-suggestion-original">Ȼ {s.originalCoins}</span>
                    <span className="checkin-suggestion-discounted">Ȼ {s.discountedCoins}</span>
                  </div>
                  {unaffordable ? (
                    <span className="checkin-suggestion-unaffordable-tag">Not enough coins</span>
                  ) : (
                    <span className="checkin-suggestion-save-badge">Save Ȼ {s.savingCoins}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Modal.Body>

      <Modal.Footer className="border-0 checkin-suggestion-footer">
        <div className="checkin-suggestion-summary">
          <span>
            Selected: <strong>Ȼ {suggestionStore.selectionTotal}</strong>
          </span>
          <span className="checkin-suggestion-summary-sep">·</span>
          <span>
            You save: <strong className="checkin-suggestion-summary-save">Ȼ {suggestionStore.selectionSavings}</strong>
          </span>
          <span className="checkin-suggestion-summary-sep">·</span>
          <span>Ȼ {Math.max(0, suggestionStore.coinsRemaining)} left</span>
        </div>
        <Button
          variant="primary"
          className="checkin-suggestion-confirm w-100"
          disabled={suggestionStore.selectedKeys.length === 0 || isConfirming}
          onClick={handleConfirm}
        >
          {isConfirming ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              {suggestionStore.confirmTotal > 1
                ? `Booking ${Math.min(suggestionStore.confirmDone + 1, suggestionStore.confirmTotal)} of ${suggestionStore.confirmTotal}…`
                : 'Booking…'}
            </>
          ) : (
            `Confirm selected${
              suggestionStore.selectedKeys.length > 0 ? ` (${suggestionStore.selectedKeys.length})` : ''
            }`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

export default CheckinSuggestionDialog;
